import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { MessageSquare, ChevronLeft, ChevronRight } from 'lucide-react';

interface ManagerNote {
  id: string;
  userId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: string;
}

interface ManagerNotesSectionProps {
  notes: ManagerNote[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onAddNote: (content: string) => Promise<void>;
  saving: boolean;
}

export const ManagerNotesSection: React.FC<ManagerNotesSectionProps> = ({
  notes,
  loading,
  currentPage,
  totalPages,
  onPageChange,
  onAddNote,
  saving,
}) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation('common');
  const [newNoteContent, setNewNoteContent] = useState('');

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    
    await onAddNote(newNoteContent);
    setNewNoteContent('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          {t('managerNotes.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Add New Note */}
          <div className="space-y-2">
            <Textarea
              placeholder={t('managerNotes.placeholder')}
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              rows={3}
            />
            <Button
              onClick={handleAddNote}
              disabled={saving || !newNoteContent.trim()}
              size="sm"
            >
              {saving ? t('managerNotes.adding') : t('managerNotes.addNote')}
            </Button>
          </div>

          {/* Notes List */}
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">{t('managerNotes.loading')}</div>
          ) : notes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('managerNotes.noNotes')}
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <div key={note.id} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{note.authorName}</span>
                    <span className="text-sm text-muted-foreground">
                      {new Date(note.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <div className="text-sm text-muted-foreground">
                {tCommon('pagination.page')} {currentPage + 1} {tCommon('pagination.of')} {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {tCommon('pagination.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                >
                  {tCommon('pagination.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
